import React from 'react';
import {StyleSheet, Text, View} from '@react-pdf/renderer';
import type {Style} from '@react-pdf/types';
import {plateTextLeaves} from './resolver/resolvePlateValue';
import type {PlateBlockNode, PlateInlineNode, PlateTextLeaf, PlateValue} from './resolver/types';

const styles = StyleSheet.create({
	h1: {fontSize: 16, marginBottom: 6, fontWeight: 700},
	p: {fontSize: 10, marginBottom: 4},
	li: {fontSize: 10, marginLeft: 8},
	bold: {fontWeight: 700},
	italic: {fontStyle: 'italic'},
	// Courier is one of the PDF standard fonts, so it needs no registration whatever document
	// font the template picked.
	code: {fontFamily: 'Courier'},
});

type Props = {
	value: PlateValue;
	baseStyle: Style;
};

function renderInline(node: PlateInlineNode, key: number): React.ReactNode {
	if ('type' in node && node.type === 'mention') {
		return (
			<Text key={key} style={styles.italic}>
				{`{${node.value}}`}
			</Text>
		);
	}
	const leaf = node as PlateTextLeaf;
	const style: Style[] = [];
	if (leaf.bold) style.push(styles.bold);
	if (leaf.italic) style.push(styles.italic);
	if (leaf.code) style.push(styles.code);
	const decorations = [leaf.underline && 'underline', leaf.strikethrough && 'line-through'].filter(Boolean);
	if (decorations.length > 0) {
		style.push({textDecoration: decorations.join(' ')} as Style);
	}
	if (style.length === 0) return leaf.text;
	return (
		<Text key={key} style={style}>
			{leaf.text}
		</Text>
	);
}

const inlineContent = (node: PlateBlockNode): React.ReactNode => plateTextLeaves(node).map(renderInline);

function renderBlock(block: PlateBlockNode, baseStyle: Style, key: number): React.ReactNode {
	switch (block.type) {
		case 'h1':
			return (
				<Text key={key} style={[baseStyle, styles.h1]}>
					{inlineContent(block)}
				</Text>
			);
		case 'ul':
		case 'ol':
			return (
				<View key={key}>
					{(block.children as PlateBlockNode[]).map((li, j) => (
						<Text key={j} style={[baseStyle, styles.li]}>
							{block.type === 'ol' ? `${j + 1}. ` : '• '}
							{inlineContent(li)}
						</Text>
					))}
				</View>
			);
		case 'li':
			return (
				<Text key={key} style={[baseStyle, styles.li]}>
					{'• '}
					{inlineContent(block)}
				</Text>
			);
		case 'p':
		default:
			return (
				<Text key={key} style={[baseStyle, styles.p]}>
					{inlineContent(block)}
				</Text>
			);
	}
}

const PlateToPdf: React.FC<Props> = ({value, baseStyle}) => (
	<View>{value.map((block, i) => renderBlock(block, baseStyle, i))}</View>
);

export default PlateToPdf;
